/**
 * Page d'accueil publique de VPC.
 *
 * Présente le service en quelques secondes : valeur claire (3 cartes),
 * 2 CTA (créer mon compte / se connecter), et un message destiné aux pros.
 *
 * Refonte R11 (mai 2026) :
 *   - Hero glass dans Card avec halo multi-modules subtle (signal "service public riche")
 *   - Pill éligibilité service public avec backdrop-blur
 *   - 3 cartes glass avec LogoMark variants module (tech/grc/urba) — signale la richesse de l'app
 *   - CTAs en boutons R2 size lg
 *   - Bloc pro en Card glass border-tech/30 pour signal "espace pro"
 *
 * Logique métier 100% préservée :
 *   - Aucune logique métier (page statique)
 *   - Utilise PublicLayout (déjà refondu en R3)
 *   - 2 CTAs : /inscription (créer compte) + /connexion (login)
 */

import { Link } from 'react-router-dom';
import { Button, Card, LogoMark, SectionEyebrow } from '@/components/ui';
import { PublicLayout } from '@/components/PublicLayout';
import { ArrowRight, ShieldCheck, MapPin, Bell, Landmark } from 'lucide-react';

export function LandingPage() {
  return (
    <PublicLayout maxWidth="2xl" centered={false}>
      <div className="space-y-12 py-8 sm:py-12 animate-fade-in">
        {/* Hero — Card glass avec halo multi-modules */}
        <section className="text-center">
          <div
            className="mx-auto rounded-r-2xl border border-border bg-surface/40 backdrop-blur-glass p-8 sm:p-12 shadow-soft"
            style={{
              backgroundImage:
                'radial-gradient(280px 140px at 20% 30%, oklch(var(--c-tech) / 0.18), transparent 70%), ' +
                'radial-gradient(280px 140px at 80% 70%, oklch(var(--c-grc) / 0.16), transparent 70%), ' +
                'linear-gradient(180deg, oklch(var(--c-surface) / 0.5), oklch(var(--c-bg-2) / 0.4))',
            }}
          >
            {/* Pill éligibilité */}
            <div className="inline-flex items-center gap-2 rounded-r-pill border border-tech/30 bg-tech-tint/60 backdrop-blur-glass px-4 py-1.5 text-xs font-medium text-tech-fg">
              <Landmark className="h-3.5 w-3.5" aria-hidden="true" />
              Service public local · sécurisé · souverain
            </div>

            <h1 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-r-tight text-ink">
              Votre commune,<br className="sm:hidden" /> à portée d'un clic.
            </h1>

            <p className="mx-auto mt-4 max-w-2xl text-base sm:text-lg text-ink-muted leading-relaxed">
              Signalez un problème, suivez son traitement, restez informé des actualités de votre
              mairie. Un service public numérique simple, gratuit et respectueux de vos données.
            </p>

            <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/inscription" className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto sm:min-w-[200px]">
                  Créer mon compte
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Button>
              </Link>
              <Link to="/connexion" className="w-full sm:w-auto">
                <Button size="lg" variant="secondary" className="w-full sm:w-auto sm:min-w-[200px]">
                  Me connecter
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* 3 cartes valeur */}
        <section
          aria-labelledby="features-title"
          className="grid grid-cols-1 sm:grid-cols-3 gap-3"
        >
          <h2 id="features-title" className="sr-only">
            Trois bonnes raisons d'utiliser Vision Pro Collectivité
          </h2>

          <Card variant="glass" padded>
            <div className="space-y-2">
              <LogoMark icon={MapPin} accent="tech" size="md" />
              <SectionEyebrow as="div">Étape 1</SectionEyebrow>
              <h3 className="font-display text-base font-semibold tracking-r-snug text-ink">
                Signaler en 30 secondes
              </h3>
              <p className="text-sm text-ink-muted leading-relaxed">
                Voirie abîmée, éclairage en panne, dépôt sauvage&nbsp;? Photo, lieu, c'est envoyé.
              </p>
            </div>
          </Card>

          <Card variant="glass" padded>
            <div className="space-y-2">
              <LogoMark icon={Bell} accent="grc" size="md" />
              <SectionEyebrow as="div">Étape 2</SectionEyebrow>
              <h3 className="font-display text-base font-semibold tracking-r-snug text-ink">
                Suivez votre demande
              </h3>
              <p className="text-sm text-ink-muted leading-relaxed">
                Reçu, planifié, en cours, résolu&nbsp;: à chaque étape, une notification.
              </p>
            </div>
          </Card>

          <Card variant="glass" padded>
            <div className="space-y-2">
              <LogoMark icon={ShieldCheck} accent="urba" size="md" />
              <SectionEyebrow as="div">Garantie</SectionEyebrow>
              <h3 className="font-display text-base font-semibold tracking-r-snug text-ink">
                Vos données protégées
              </h3>
              <p className="text-sm text-ink-muted leading-relaxed">
                Hébergement souverain en France, conformité RGPD, suppression sur demande.
              </p>
            </div>
          </Card>
        </section>

        {/* Bloc info pour les pros */}
        <Card variant="glass" padded className="border-tech/30">
          <div className="flex items-start gap-3">
            <LogoMark icon={Landmark} accent="tech" size="md" />
            <div className="flex-1">
              <SectionEyebrow as="h2" className="text-tech-fg">Espace professionnel</SectionEyebrow>
              <p className="mt-2 text-sm text-ink-muted leading-relaxed">
                <strong className="text-ink">Vous êtes agent ou élu&nbsp;?</strong> Votre compte
                professionnel vous a été remis par votre mairie. Cliquez sur «&nbsp;Me connecter&nbsp;»
                et utilisez vos identifiants habituels. Si votre commune n'utilise pas encore Vision Pro
                Collectivité, contactez-nous via votre élu référent.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </PublicLayout>
  );
}
